import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Workflow, GitBranch, FolderCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * What one control produces (Messaging Architecture v1.0, pillar three:
 * "the artifacts an auditor actually asks for").
 *
 * The four outputs are deliberately NOT a capability tour — the integration
 * library and document intelligence are inputs and live on their own pages.
 * Everything here is something a customer hands to an auditor.
 *
 * The flow treatment is the hero's, on purpose: connector paths measured from
 * real DOM anchors, a travelling dash on the shared `.flow-dash` keyframes, and
 * nodes that escalate from resting tone to Trail Teal in sequence. The control
 * node names the same cryptographic control the figure above maps to three
 * frameworks, so the two halves of this section read as one argument.
 *
 * SELECTION MODEL — read this before changing the layout.
 * A card rests at its name alone and opens its body and "why" in a full-width
 * panel that is a grid item placed immediately after the selected card. That
 * one placement gives two behaviours for free:
 *
 *   lg (4 columns, connectors drawn)  every card is in row 1, so the panel
 *                                     lands in row 2 and NO CARD MOVES. The
 *                                     fan-out is the argument — one control,
 *                                     four artifacts — so collapsing it into a
 *                                     list or sliding it into a rail throws
 *                                     away the thing the section exists to
 *                                     show. Selecting never re-runs measure();
 *                                     it only re-paints stroke and opacity.
 *   below lg (1 or 2 columns)         the panel lands directly under the card
 *                                     you tapped, which is the accordion
 *                                     behaviour a narrow screen needs. Cards
 *                                     below it shift, which is fine because
 *                                     the connectors are hidden there anyway.
 *
 * measure() still runs on resize, as it always did.
 */
const OUTPUTS = [
  { key: 'narrative', Icon: FileText },
  { key: 'process', Icon: GitBranch },
  { key: 'workflow', Icon: Workflow },
  { key: 'evidence', Icon: FolderCheck },
] as const;

const STEP_MS = 520;

// Visual order inside the grid. Cards take the odd slots and the detail panel
// takes the even slot right after its own card, so on a narrow screen the panel
// opens directly under the card you tapped. At lg the panel jumps to order-9,
// behind all four cards, so it lands in row two and no card moves. These are
// written as literal class names because Tailwind's JIT reads source text.
const CARD_ORDER = ['order-1', 'order-3', 'order-5', 'order-7'] as const;
const PANEL_ORDER = ['order-2', 'order-4', 'order-6', 'order-8'] as const;

const ControlOutputs = () => {
  const { t } = useTranslation();
  const wrapRef = useRef<HTMLDivElement>(null);
  const controlRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [paths, setPaths] = useState<string[]>([]);
  const [active, setActive] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);

  // Connector geometry, measured rather than guessed, so the lines survive
  // translation, wrapping and any later spacing change.
  const measure = useCallback(() => {
    const wrap = wrapRef.current;
    const control = controlRef.current;
    if (!wrap || !control) return;
    const w = wrap.getBoundingClientRect();
    if (!w.width) return;
    setBox({ w: w.width, h: w.height });
    const c = control.getBoundingClientRect();
    const fx = c.left - w.left + c.width / 2;
    const fy = c.bottom - w.top;
    const next: string[] = [];
    cardRefs.current.forEach((el) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      const tx = r.left - w.left + r.width / 2;
      const ty = r.top - w.top;
      const dy = Math.max(ty - fy, 1);
      next.push(`M ${fx} ${fy} C ${fx} ${fy + dy * 0.55}, ${tx} ${ty - dy * 0.55}, ${tx} ${ty}`);
    });
    setPaths(next);
  }, []);

  useLayoutEffect(() => {
    measure();
    const ro = new ResizeObserver(measure);
    if (wrapRef.current) ro.observe(wrapRef.current);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [measure]);

  // The sequence runs once, when the section is actually on screen. Anyone who
  // has asked for reduced motion gets the finished state immediately.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { setActive(OUTPUTS.length); return; }
    const timers: ReturnType<typeof setTimeout>[] = [];
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        io.disconnect();
        OUTPUTS.forEach((_, i) => {
          timers.push(setTimeout(() => setActive(i + 1), 260 + i * STEP_MS));
        });
      },
      // threshold alone fails on a section taller than the viewport, which
      // is every phone: the ratio can never reach it. Fire on the section
      // crossing the lower fifth of the window instead.
      { threshold: 0, rootMargin: '0px 0px -20% 0px' },
    );
    io.observe(el);
    return () => { io.disconnect(); timers.forEach(clearTimeout); };
  }, []);

  const chosen = selected === null ? null : OUTPUTS[selected];

  return (
    <div ref={wrapRef} className="relative">
      {/* The section's contour texture runs the whole band, so the fan lays a
          soft scrim of its own ground underneath. Without it the topo lines
          cross the connector curves and both become harder to read. Radial, so
          it fades out rather than ending on an edge. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-x-8 -inset-y-6 -z-10"
        style={{
          background:
            'radial-gradient(60% 55% at 50% 45%, hsl(var(--brand-ink) / 0.92) 0%, hsl(var(--brand-ink) / 0.72) 55%, transparent 100%)',
        }}
      />
      {/* Connectors: desktop only, where the fan actually reads. */}
      {box.w > 0 && paths.length > 0 && (
        <svg
          aria-hidden
          className="pointer-events-none absolute inset-0 z-0 hidden h-full w-full lg:block"
          width={box.w}
          height={box.h}
          viewBox={`0 0 ${box.w} ${box.h}`}
        >
          {paths.map((d, i) => {
            // Before anything is chosen the intro sequence drives the line.
            // After, the chosen line carries the flow and the rest step back,
            // so the diagram keeps arguing while the detail is open.
            const lit = selected === null ? i < active : selected === i;
            const muted = selected !== null && selected !== i;
            return (
              <g key={d}>
                <path
                  d={d}
                  fill="none"
                  stroke={lit ? 'hsl(var(--trail-teal))' : 'hsl(var(--brand-ink-foreground) / 0.22)'}
                  strokeWidth={lit ? 1.5 : 1}
                  vectorEffect="non-scaling-stroke"
                  className="transition-all duration-500"
                  opacity={muted ? 0.35 : 1}
                />
                <path
                  d={d}
                  fill="none"
                  stroke="hsl(var(--trail-teal))"
                  strokeWidth={lit ? 2 : 1.5}
                  strokeLinecap="round"
                  strokeDasharray="4 26"
                  vectorEffect="non-scaling-stroke"
                  className="flow-dash"
                  style={{ animationDelay: `${i * 0.45}s` }}
                  opacity={lit ? 0.9 : muted ? 0 : 0.25}
                />
              </g>
            );
          })}
        </svg>
      )}

      {/* The control, named as in the figure above. */}
      <div className="relative z-10 flex justify-center">
        <div
          ref={controlRef}
          className="w-full max-w-xs border border-trail-teal/60 bg-brand-ink-raised p-5 text-center"
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-trail-teal">
            {t('secondFramework.figureOneLabel')}
          </p>
          <p className="mt-2 text-base font-semibold text-brand-ink-foreground">
            {t('secondFramework.controls.crypto.name')}
          </p>
          <p className="mt-1 text-sm text-brand-ink-foreground/60">
            {t('secondFramework.controls.crypto.sub')}
          </p>
        </div>
      </div>

      <div className="relative z-10 mt-10 grid gap-px bg-brand-ink-foreground/15 sm:grid-cols-2 lg:mt-20 lg:grid-cols-4">
        {OUTPUTS.map(({ key, Icon }, i) => {
          const lit = selected === null ? i < active : selected === i;
          const muted = selected !== null && selected !== i;
          const isOpen = selected === i;
          return (
            <Fragment key={key}>
              <button
                type="button"
                data-output={key}
                ref={(el) => { cardRefs.current[i] = el; }}
                aria-expanded={isOpen}
                aria-controls={isOpen ? 'control-output-detail' : undefined}
                onClick={() => setSelected(isOpen ? null : i)}
                className={cn(
                  CARD_ORDER[i],
                  'flex h-full flex-col items-start gap-3 p-6 text-left transition-all duration-300',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-trail-teal',
                  isOpen ? 'bg-trail-teal/15' : 'bg-brand-ink hover:bg-brand-ink-raised',
                  muted && 'opacity-55',
                  !lit && selected === null && 'opacity-60',
                )}
              >
                <Icon
                  className={cn('h-6 w-6 transition-colors duration-500',
                    lit ? 'text-trail-teal' : 'text-brand-ink-foreground/40')}
                  aria-hidden
                />
                <h3 className="text-base font-semibold text-brand-ink-foreground">
                  {t(`controlOutputs.${key}.name`)}
                </h3>
              </button>

              {isOpen && chosen && (
                <div
                  id="control-output-detail"
                  role="region"
                  aria-label={t(`controlOutputs.${chosen.key}.name`)}
                  className={cn(
                    PANEL_ORDER[i],
                    'lg:order-9 col-span-full border-t-2 border-trail-teal bg-brand-ink-raised p-6',
                    'duration-300 animate-in fade-in slide-in-from-top-1 motion-reduce:animate-none',
                  )}
                >
                  <h4 className="text-base font-semibold text-brand-ink-foreground">
                    {t(`controlOutputs.${chosen.key}.name`)}
                  </h4>
                  <p className="mt-2 max-w-3xl text-sm leading-relaxed text-brand-ink-foreground/75">
                    {t(`controlOutputs.${chosen.key}.body`)}
                  </p>
                  <p className="mt-4 max-w-3xl border-l-2 border-trail-teal pl-3 text-sm leading-relaxed text-brand-ink-foreground/90">
                    {t(`controlOutputs.${chosen.key}.why`)}
                  </p>
                </div>
              )}
            </Fragment>
          );
        })}
      </div>

      <p className="relative z-10 mt-8 max-w-3xl text-base text-brand-ink-foreground/70">
        {t('controlOutputs.footnote')}
      </p>
    </div>
  );
};

export default ControlOutputs;
